import { Button } from 'shadcn/components/ui/Button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'shadcn/components/ui/Dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from 'shadcn/components/ui/tooltip';

import { CONTENT_VARIANT_CATEGORIES, CONTENT_VARIANTS } from './constants';

/**
 * Dialog for adding new content to an aisle.
 *
 * @param {string} dialogAisleID - The ID of the aisle to add content to.
 * @param {Object} existingBanner - Existing banner data.
 * @param {Function} handleAddNewEditor - Callback function to add a new editor.
 * @param {Function} handleClose - Callback function to close the dialog.
 * @param {Object} newBannerEditors - New banner editors data.
 */
export default function AddContentDialog({
    dialogAisleID,
    existingBanner,
    existingCarousel,
    handleAddNewEditor,
    handleClose,
    newBannerEditors,
    newCarouselEditors,
}) {
    const hasBanner = existingBanner || (newBannerEditors && newBannerEditors.length > 0);
    const hasCarousel = existingCarousel || (newCarouselEditors && newCarouselEditors.length > 0);

    return (
        <Dialog open={!!dialogAisleID} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-3xl">
                <DialogHeader>
                    <DialogTitle>Add Aisle Content</DialogTitle>
                    <DialogDescription>Select a content type to add to the aisle.</DialogDescription>
                </DialogHeader>

                <div className="flex flex-col gap-8">
                    {Object.entries(CONTENT_VARIANT_CATEGORIES).map(([, category]) => (
                        <div key={category.name} className="flex flex-col gap-4">
                            <h3 className="mb-0 text-lg font-medium capitalize">
                                {category.name}{' '}
                                <span className="text-sm text-muted-foreground font-normal lowercase">
                                    ({category.variants.length} variation{category.variants.length === 1 ? '' : 's'})
                                </span>
                            </h3>
                            <div className="grid grid-cols-3 gap-4">
                                {category.variants.map((variantID) => {
                                    const variant = CONTENT_VARIANTS[variantID];
                                    if (!variant) return null;

                                    const isBannerVariant = variant.category === 'banner';
                                    const isCarouselVariant = variant.category === 'carousel';
                                    const isDisabled =
                                        (isBannerVariant && hasBanner) || (isCarouselVariant && hasCarousel);

                                    const buttonElement = (
                                        <button
                                            key={variantID}
                                            onClick={() => {
                                                handleAddNewEditor({ aisleID: dialogAisleID, variantID });
                                                handleClose();
                                            }}
                                            disabled={isDisabled}
                                            className="flex flex-col items-center gap-6 w-full p-3 border rounded-md bg-gray-100 transition-colors hover:border-primary hover:bg-border hover:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-gray-100 disabled:hover:border-border"
                                            type="button"
                                        >
                                            <div className="flex-1 p-2 rounded-md bg-background">
                                                <img
                                                    src={variant.thumbnail}
                                                    alt={variant.name}
                                                    className="object-contain w-full h-16"
                                                />
                                            </div>
                                            <p className="font-medium">{variant.name}</p>
                                        </button>
                                    );

                                    if (isDisabled) {
                                        return (
                                            <Tooltip key={variantID}>
                                                <TooltipTrigger asChild>{buttonElement}</TooltipTrigger>
                                                <TooltipContent className="text-md z-[10000]">
                                                    {isBannerVariant && 'Only one banner is allowed per aisle'}
                                                    {isCarouselVariant && 'Only one carousel is allowed per aisle'}
                                                </TooltipContent>
                                            </Tooltip>
                                        );
                                    }

                                    return buttonElement;
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={handleClose}>
                        Cancel
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
